<script setup>
import { i18n } from 'boot/i18n';
import {
  HrErrorBanner,
  HrIcon,
  HrInfoBanner,
  HrSuccessBanner,
  HrWarningBanner,
} from 'src/design-system';
import { computed } from 'vue';

defineOptions({ name: 'AuditVerdictBanner' });

const props = defineProps({
  tone: {
    type: String,
    required: true,
    validator: (value) => ['success', 'warning', 'error', 'info'].includes(value),
  },
  statusLabel: {
    type: String,
    required: true,
  },
  statusIcon: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: () => i18n.t('noteAuditor.feedback.cardTitle'),
  },
});

const BANNER_COMPONENTS = {
  success: HrSuccessBanner,
  warning: HrWarningBanner,
  error: HrErrorBanner,
  info: HrInfoBanner,
};

const TONE_CLASSES = {
  success: 'hr-success',
  warning: 'hr-warning',
  error: 'hr-error',
  info: 'hr-info',
};

const bannerComponent = computed(() => BANNER_COMPONENTS[props.tone]);
const toneClass = computed(() => TONE_CLASSES[props.tone]);
</script>

<template>
  <div class="audit-verdict-banner hr-surface hr-outline-variant col-12">
    <div class="audit-verdict-banner__summary">
      <div class="audit-verdict-banner__summary-header">
        <hr-icon
          name="auto_awesome"
          size="sm"
        />
        <span class="hr-title-medium audit-verdict-banner__summary-title">
          {{ title }}
        </span>

        <span
          class="audit-verdict-banner__chip hr-label-large"
          :class="toneClass"
        >
          <hr-icon
            :name="statusIcon"
            size="xs"
          />
          {{ statusLabel }}
        </span>

        <!-- @slot Controls for this surface: feedback on the leader side, a re-run at check-out. -->
        <slot name="actions" />
      </div>

      <component
        :is="bannerComponent"
        :icon="statusIcon"
        :message="message"
      />
    </div>

    <slot />
  </div>
</template>

<style lang="scss" scoped>
.audit-verdict-banner {
  border-radius: var(--app-shape-corner-large, 16px);
  overflow: hidden;

  &__summary {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;

    &-header {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &-title {
      flex: 1;
    }
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 12px;
    border-radius: var(--app-shape-corner-full, 999px);
    white-space: nowrap;
  }
}
</style>
